import { formatDate, IconCheckmarkRoundedFilled, IconErrorFilled } from '@repo/design-system-kit';
import { useIntl } from 'react-intl';
import type { ReactNode } from 'react';
import type { InvoiceStatus } from 'receivable-invoices-js/react/api/models/receivable-invoice';
import type { QuoteStatus } from 'receivable-invoices-js/react/api/models/quote';
import styles from './status-and-date.module.css';

interface StatusAndDateProps {
  isQuote: boolean;
  status: InvoiceStatus | QuoteStatus;
  date: string;
}

export function StatusAndDate({ isQuote, status, date }: StatusAndDateProps): ReactNode {
  const { locale, formatMessage } = useIntl();

  const formattedDate = formatDate({ date, locale, token: 'date-year-s' });
  const hasExpired = new Date(date) < new Date();

  if (isQuote) {
    switch (status) {
      case 'pending_approval':
        return (
          <p className={`${styles.formattedDate} body-1`} data-test-status-date>
            {hasExpired
              ? formatMessage({ id: 'client-portal.expired-on' }, { date: formattedDate })
              : formatMessage({ id: 'quote.preview.expiration-label' }, { date: formattedDate })}
          </p>
        );
      case 'approved':
        return (
          <>
            <IconCheckmarkRoundedFilled
              color="var(--content-success)"
              data-test-status-icon="approved"
            />
            <p className={`${styles.formattedDate} body-1`} data-test-status-date>
              {formatMessage({ id: 'client-portal.approved-on' }, { date: formattedDate })}
            </p>
          </>
        );
      case 'canceled':
        return (
          <>
            <IconErrorFilled color="var(--content-error)" data-test-status-icon="canceled" />
            <p className={`${styles.formattedDate} body-1`} data-test-status-date>
              {formatMessage({ id: 'client-portal.canceled-on.quote' }, { date: formattedDate })}
            </p>
          </>
        );
      default:
        return null;
    }
  } else {
    switch (status) {
      case 'unpaid':
        return (
          <p className={`${styles.formattedDate} body-1`} data-test-status-date>
            {formatMessage({ id: 'client-portal.due-date' }, { date: formattedDate })}
          </p>
        );
      case 'paid':
        return (
          <>
            <IconCheckmarkRoundedFilled
              color="var(--content-success)"
              data-test-status-icon="paid"
            />
            <p className={`${styles.formattedDate} body-1`} data-test-status-date>
              {formatMessage({ id: 'client-portal.paid-on.invoice' }, { date: formattedDate })}
            </p>
          </>
        );
      case 'canceled':
        return (
          <>
            <IconErrorFilled color="var(--content-error)" data-test-status-icon="canceled" />
            <p className={`${styles.formattedDate} body-1`} data-test-status-date>
              {formatMessage({ id: 'client-portal.canceled-on.invoice' }, { date: formattedDate })}
            </p>
          </>
        );
      default:
        return null;
    }
  }
}
